/**
 * TimelinePositionManager - Utility class for handling timeline playhead positioning
 * Centralizes all logic related to converting between pixel positions and timeline timestamps
 */
export class TimelinePositionManager {
  // Constants
  private readonly TRACK_LABEL_WIDTH: number = 120; // Width of track labels in pixels

  constructor(
    private readonly timelineScale: number, // Pixels per second
    private readonly duration: number, // Total timeline duration in seconds
    private readonly containerRef: React.RefObject<HTMLDivElement> // Reference to track container
  ) {}

  /**
   * Converts screen coordinates to timeline position
   * @param clientX The client X coordinate from a mouse event
   * @returns Timeline position in seconds, or null if conversion failed
   */
  public clientXToTimePosition(clientX: number): number | null {
    // Get container bounds
    const rect = this.containerRef.current?.getBoundingClientRect();
    if (!rect) return null;

    // Calculate position relative to the container
    const clickX = clientX - rect.left;
    
    // Skip if clicked in the label area
    if (clickX <= this.TRACK_LABEL_WIDTH) return null;
    
    // Convert to time, accounting for label width
    const contentClickX = clickX - this.TRACK_LABEL_WIDTH;
    const time = contentClickX / this.timelineScale;
    
    // Clamp to valid range
    return this.clampTimeToValidRange(time);
  }

  /**
   * Converts a time value to screen X position
   * @param time Time in seconds
   * @returns Screen X position in pixels relative to container left edge
   */
  public timeToScreenPosition(time: number): number {
    return this.TRACK_LABEL_WIDTH + (time * this.timelineScale);
  }

  /**
   * Clamps a time value to the valid timeline range
   * @param time Time value in seconds
   * @returns Clamped time value
   */
  public clampTimeToValidRange(time: number): number {
    return Math.max(0, Math.min(time, this.duration));
  }
  
  /**
   * Creates a positioning manager with updated scale
   * @param newScale New time scale in pixels per second
   * @returns A new TimelinePositionManager with the updated scale
   */
  public withTimeScale(newScale: number): TimelinePositionManager {
    return new TimelinePositionManager(newScale, this.duration, this.containerRef);
  }
  
  /**
   * Creates a positioning manager with updated duration
   * @param newDuration New duration in seconds
   * @returns A new TimelinePositionManager with the updated duration
   */
  public withDuration(newDuration: number): TimelinePositionManager {
    return new TimelinePositionManager(this.timelineScale, newDuration, this.containerRef);
  }
}